/**
 * OAuth Token Exchange Utilities
 *
 * SECURITY: Token exchange requires a `client_secret` / `app_secret`. Reading those
 * via `import.meta.env.VITE_*` here would inline them into the client bundle (that's
 * how the Facebook App Secret and Gemini key once leaked). So the exchange runs
 * server-side: these functions POST to /api/oauth/exchange (api/_lib/oauthExchange.ts),
 * which holds the secrets in process.env and returns only the tokens.
 *
 * The endpoint is dormant until its env pair is set (VITE_*_APP_ID/CLIENT_ID +
 * *_APP_SECRET/CLIENT_SECRET) — see .env.example.
 */

import { getSupabaseClient } from '@/config';

export interface FacebookTokenExchangeResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface GoogleTokenExchangeResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

export interface TikTokTokenExchangeResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

/** The caller's session, which the exchange proxy requires — same helper shape
 *  as the validate proxies, which are gated the same way. */
const authHeader = async (): Promise<Record<string, string>> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** POST the code to the server-side exchange proxy; the secret never touches the browser. */
async function postExchange(
  platform: string,
  code: string,
  redirectUri: string,
): Promise<{ access_token: string; refresh_token?: string; expires_in?: number; token_type?: string }> {
  const resp = await fetch('/api/oauth/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ platform, code, redirect_uri: redirectUri }),
  });
  const data = (await resp.json().catch(() => null)) as
    | { access_token?: string; refresh_token?: string; expires_in?: number; token_type?: string; error?: string }
    | null;
  if (!resp.ok || !data?.access_token) {
    throw new Error(data?.error || `OAuth token exchange failed (HTTP ${resp.status})`);
  }
  return { access_token: data.access_token, refresh_token: data.refresh_token, expires_in: data.expires_in, token_type: data.token_type };
}

export async function exchangeFacebookToken(
  code: string,
  redirectUri: string
): Promise<FacebookTokenExchangeResponse> {
  const t = await postExchange('facebook', code, redirectUri);
  return { access_token: t.access_token, token_type: t.token_type ?? 'bearer', expires_in: t.expires_in ?? 0 };
}

export async function exchangeGoogleToken(
  code: string,
  redirectUri: string
): Promise<GoogleTokenExchangeResponse> {
  const t = await postExchange('google_ads', code, redirectUri);
  return { access_token: t.access_token, refresh_token: t.refresh_token, token_type: t.token_type ?? 'Bearer', expires_in: t.expires_in ?? 0 };
}

export async function exchangeTikTokToken(
  code: string,
  redirectUri: string
): Promise<TikTokTokenExchangeResponse> {
  const t = await postExchange('tiktok', code, redirectUri);
  return { access_token: t.access_token, refresh_token: t.refresh_token, token_type: t.token_type ?? 'Bearer', expires_in: t.expires_in ?? 0 };
}

/**
 * Get token expiry date from expires_in seconds
 * @param expiresIn - Number of seconds until token expires
 * @returns ISO string of expiry date
 */
export function getTokenExpiryDate(expiresIn: number): string {
  // Subtract 5 minutes for safety margin
  const expiresAt = Date.now() + (expiresIn - 300) * 1000;
  return new Date(expiresAt).toISOString();
}
